import axios from 'axios';
import { BACKEND_URL } from 'src/settings';

import { SupplierApplicationFormData } from '../model/schema';

interface SupplierApplicationPayload {
  formData: SupplierApplicationFormData;
  catalogFiles: File[];
  photoFiles: File[];
}

/**
 * Submits a supplier application to the public backend endpoint using multipart/form-data.
 * No auth token required.
 */
const submitSupplierApplication = async ({
  formData,
  catalogFiles,
  photoFiles
}: SupplierApplicationPayload): Promise<void> => {
  const body = new FormData();

  // Append all text/number fields; join arrays as comma-separated strings
  (
    Object.entries(formData) as [keyof SupplierApplicationFormData, unknown][]
  ).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    if (Array.isArray(value)) {
      body.append(key, value.join(', '));
    } else {
      body.append(key, String(value));
    }
  });

  // Append files
  catalogFiles.forEach((file) => body.append('catalog_files', file));
  photoFiles.forEach((file) => body.append('photo_files', file));

  await axios.post(`${BACKEND_URL}/api/supplier-application/`, body, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export default submitSupplierApplication;

// backend/suppliers/api.py > supplier_application (error responses)
export interface ServerError {
  response?: {
    data?: { error?: string; field?: keyof SupplierApplicationFormData };
  };
}

/**
 * Returns the field and message of a submission error about a single field
 * (e.g. a website another supplier already uses), or null for any other error.
 */
export const serverFieldError = (
  error: unknown
): { field: keyof SupplierApplicationFormData; message: string } | null => {
  const data = (error as ServerError | null)?.response?.data;

  if (!data?.field || !data.error) return null;

  return { field: data.field, message: data.error };
};
